'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { useId, useRef } from 'react';
import { useFieldContext } from '@/lib/form/contexts';

/** Accepted extensions per 10.03.04.03 AC 7. */
export const ACCEPTED_FILE_EXTENSIONS = ['.jpg', '.jpeg', '.pdf'] as const;

/** 5 MB per file, per 10.03.04.03 AC 7. There is no aggregate cap. */
export const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

const ACCEPT_ATTR = ACCEPTED_FILE_EXTENSIONS.join(',');

/**
 * Returns a German validation message, or `null` when the file is acceptable.
 * Exported so the same rules can be asserted in tests and reused by a form
 * validator rather than duplicated.
 */
export const validateFile = (file: File): string | null => {
  const name = file.name.toLowerCase();
  const hasAcceptedExtension = ACCEPTED_FILE_EXTENSIONS.some((ext) =>
    name.endsWith(ext),
  );

  if (!hasAcceptedExtension) {
    return 'Nur JPG- oder PDF-Dateien sind erlaubt.';
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return 'Die Datei ist größer als 5 MB.';
  }
  return null;
};

interface FileInputProps {
  label: string;
  /** Shown when no file is selected. */
  emptyLabel?: string;
  buttonLabel?: string;
}

/**
 * Single file picker bound to a TanStack Form field holding `File | null`.
 *
 * Validation runs on selection so the applicant sees the problem immediately
 * rather than on submit, and a rejected file is not written into form state.
 */
export const FileInput = ({
  label,
  emptyLabel = 'Keine ausgewählt',
  buttonLabel = 'Datei auswählen',
}: FileInputProps) => {
  const field = useFieldContext<File | null>();
  const inputRef = useRef<HTMLInputElement>(null);
  const id = useId();
  const errorId = `${id}-error`;
  const selected = field.state.value;
  const error = field.state.meta.errors[0]?.message;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;

    if (file === null) {
      field.handleChange(null);
      return;
    }

    const problem = validateFile(file);
    if (problem !== null) {
      // Keep the field empty and surface the reason. Resetting the native
      // input lets the applicant re-pick the same filename after fixing it.
      field.handleChange(null);
      field.setErrorMap({ onChange: { message: problem } });
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      return;
    }

    field.handleChange(file);
  };

  return (
    <div>
      <span className="mb-1 block text-sm">{label}</span>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="rounded-input border border-[rgba(40,25,10,0.2)] bg-white px-4 py-2 text-sm font-medium"
          aria-describedby={error ? errorId : undefined}
        >
          {buttonLabel}
        </button>
        <span
          className={classNames(
            'truncate text-sm',
            selected ? 'text-foreground' : 'text-gray-500',
          )}
        >
          {selected ? selected.name : emptyLabel}
        </span>
      </div>
      <input
        ref={inputRef}
        id={id}
        type="file"
        name={field.name}
        accept={ACCEPT_ATTR}
        onChange={handleChange}
        onBlur={field.handleBlur}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        className="hidden"
      />
      {error && (
        <p id={errorId} className="mt-1 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};
